import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Wrench,
  Shield,
  Palette,
  LayoutGrid,
  Database,
  Zap,
  FileCode,
  Users,
  CreditCard,
  Package,
  Building2,
  Settings,
  BookOpen,
  BarChart3,
  HeadphonesIcon,
  Lock,
  Eye,
  Boxes,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ChangelogEntryProps {
  version: string;
  date: string;
  title: string;
  description?: string;
  isLatest?: boolean;
  changes: {
    type: "added" | "improved" | "fixed" | "security" | "design";
    category: string;
    items: string[];
  }[];
}

const typeConfig = {
  added: {
    label: "Added",
    color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    icon: Sparkles,
  },
  improved: {
    label: "Improved",
    color: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    icon: Zap,
  },
  fixed: {
    label: "Fixed",
    color: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    icon: Wrench,
  },
  security: {
    label: "Security",
    color: "bg-red-500/10 text-red-600 border-red-500/20",
    icon: Shield,
  },
  design: {
    label: "Design",
    color: "bg-violet-500/10 text-violet-600 border-violet-500/20",
    icon: Palette,
  },
};

function ChangelogEntry({
  version,
  date,
  title,
  description,
  isLatest,
  changes,
}: ChangelogEntryProps) {
  const versionId = version.replace(/\./g, "-");

  // Group changes by type for cleaner sections
  const grouped = changes.reduce<Record<string, typeof changes>>((acc, change) => {
    if (!acc[change.type]) acc[change.type] = [];
    acc[change.type].push(change);
    return acc;
  }, {});

  // Type ordering
  const typeOrder: (keyof typeof typeConfig)[] = ["added", "improved", "fixed", "security", "design"];

  // Summary counts
  const totalItems = changes.reduce((sum, c) => sum + c.items.length, 0);

  return (
    <div id={versionId} className="relative pl-8 pb-12 last:pb-0 scroll-mt-6">
      {/* Timeline line */}
      <div className="absolute left-[11px] top-3 bottom-0 w-0.5 bg-border last:hidden" />

      {/* Timeline dot */}
      <div
        className={cn(
          "absolute left-0 top-1.5 h-6 w-6 rounded-full border-2 flex items-center justify-center",
          isLatest
            ? "bg-primary border-primary text-primary-foreground"
            : "bg-background border-muted-foreground/30"
        )}
      >
        {isLatest && <Check className="h-3 w-3" />}
      </div>

      {/* Content */}
      <div className="space-y-4">
        {/* Version header */}
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={isLatest ? "default" : "outline"} className="text-sm font-mono">
            {version}
          </Badge>
          <span className="text-sm text-muted-foreground">{date}</span>
          {isLatest && (
            <Badge variant="secondary" className="text-xs">
              Latest
            </Badge>
          )}
        </div>

        <div>
          <h3 className="text-xl font-semibold">{title}</h3>
          {description && (
            <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{description}</p>
          )}
        </div>

        {/* Summary chips */}
        <div className="flex flex-wrap gap-2">
          {typeOrder.map((type) => {
            const group = grouped[type];
            if (!group) return null;
            const config = typeConfig[type];
            const Icon = config.icon;
            const itemCount = group.reduce((sum, c) => sum + c.items.length, 0);
            return (
              <div
                key={type}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
                  config.color,
                )}
              >
                <Icon className="h-3 w-3" />
                {itemCount} {config.label}
              </div>
            );
          })}
          <div className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
            {totalItems} total changes
          </div>
        </div>

        {/* Grouped change sections */}
        <div className="space-y-5">
          {typeOrder.map((type) => {
            const group = grouped[type];
            if (!group) return null;
            const config = typeConfig[type];
            const Icon = config.icon;
            return (
              <ChangeTypeSection
                key={type}
                type={type}
                config={config}
                Icon={Icon}
                categories={group}
                defaultOpen={isLatest}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Collapsible section for a change type (Added, Fixed, etc.) */
function ChangeTypeSection({
  type,
  config,
  Icon,
  categories,
  defaultOpen = false,
}: {
  type: string;
  config: { label: string; color: string };
  Icon: React.ComponentType<{ className?: string }>;
  categories: { category: string; items: string[] }[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const itemCount = categories.reduce((sum, c) => sum + c.items.length, 0);

  // Border color per type
  const borderColors: Record<string, string> = {
    added: "border-l-emerald-500",
    improved: "border-l-blue-500",
    fixed: "border-l-amber-500",
    security: "border-l-red-500",
    design: "border-l-violet-500",
  };

  return (
    <div className={cn("border-l-[3px] rounded-r-md bg-muted/30", borderColors[type] || "border-l-muted-foreground")}>
      {/* Section header — clickable to expand/collapse */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-muted/50 transition-colors rounded-r-md"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <Badge variant="outline" className={cn("gap-1 shrink-0", config.color)}>
          <Icon className="h-3 w-3" />
          {config.label}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {categories.length} {categories.length === 1 ? "category" : "categories"} &middot; {itemCount} items
        </span>
      </button>

      {/* Expandable content */}
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4">
          {categories.map((change, idx) => (
            <div key={idx}>
              <h4 className="text-sm font-semibold mb-2">{change.category}</h4>
              <ul className="space-y-1.5 ml-0.5">
                {change.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Circle className="h-1.5 w-1.5 fill-current shrink-0 mt-[7px]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const changelog: ChangelogEntryProps[] = [
  {
    version: "v1.0.0-rc.3",
    date: "March 31, 2026",
    title: "VL Automation Integration, Email Template Branding, Privacy Policy & Logo Upload",
    description: "RC3 — Integrates VL Automation bot for live iServeU portal data (product details, card balance), adds full branding controls to email templates (colors, logo upload, fonts, footer), introduces a public-facing privacy policy page, and enhances the email template editor with logo upload and send history.",
    isLatest: true,
    changes: [
      {
        type: "added",
        category: "VL Automation Integration",
        items: [
          "New `server/services/automation.ts` service layer wrapping Central API automation endpoints with inline polling and 24-hour caching for product details",
          "New `server/routes/automation.ts` with 8 endpoints: 6 Central API proxies (execute, poll, retry, list tasks, list flows, health) + 2 integrated endpoints (product-details, card-balance)",
          "Product detail view: 'iServeU Portal Data' section with 'Fetch from Portal' button — retrieves 36 live fields (BIN, limits, KYC type, velocity rules) from the iServeU portal via browser automation, cached for 24 hours",
          "Card detail view: 'Portal Card Balance' section with 'Check Balance' button — retrieves live card balance from iServeU portal, no caching",
          "Card balance endpoint accepts `customer_id` parameter and automatically looks up the customer's phone number for the automation bot",
          "New API key scopes: `automation:execute` (submit flows, retry tasks) and `automation:read` (poll status, list tasks/flows)",
        ],
      },
      {
        type: "added",
        category: "Email Template Branding & Logo Upload",
        items: [
          "Full per-template branding controls in Settings tab: primary/secondary brand colors with color pickers, header background override, logo URL/upload, font family dropdown, footer text",
          "Logo upload component with drag-and-drop, file upload, Vercel Blob storage, and URL preview with filename extraction",
          "Live preview applies branding in real-time: color replacement, logo injection, font swap, footer text",
          "Upload endpoint `POST /api/upload/logo` with 2MB limit and image-only validation via multer + Vercel Blob",
          "Email send history tab with stats cards, search/filter, data table with pagination, and status badges",
        ],
      },
      {
        type: "added",
        category: "Privacy Policy & Legal",
        items: [
          "Public-facing privacy policy page at `/privacy-policy` — accessible without authentication",
          "10 policy sections with card-based layout: Information Collection, Usage, Data Sharing, Security, Retention, Your Rights (GDPR/CCPA), Cookies, Children's Privacy, Changes, Contact",
          "Standalone page with branded header bar and back navigation",
          "Admin login footer: Privacy link now navigates to `/privacy-policy`",
          "Portal login: Privacy Policy text now links to `/privacy-policy`",
          "Sidebar 'More' section includes Privacy Policy link for all roles",
        ],
      },
      {
        type: "improved",
        category: "Developer Experience",
        items: [
          "API Keys page now shows 8 scopes including automation:execute and automation:read",
          "Automation proxy routes follow the same pattern as partner API routes for consistency",
          "Product detail and card detail views load automation data on-demand (not by default) to avoid blocking page load",
        ],
      },
    ],
  },
  {
    version: "v1.0.0-rc.2",
    date: "March 14, 2026",
    title: "Multi-Tenant Security Hardening, Unified Transactions & Dashboard KPI Fixes",
    description: "RC2 — Closes 17+ cross-tenant data leakage vectors on single-item API routes with a new verifyOrgScope() helper, unifies admin transaction views across card, wallet, and top-up sources into a single filterable table, corrects dashboard KPI calculations for Amount Funded / Transactions / Total Volume, removes all client-side demo/mock data, and adds Sentry error tracking.",
    isLatest: false,
    changes: [
      {
        type: "security",
        category: "Multi-Tenant Access Control (17+ Routes)",
        items: [
          "New `verifyOrgScope()` server-side helper enforces org/BU boundaries on every single-item lookup route — checks businessUnitId → organizationId → SUPER/FINANCE role → 403",
          "Customer routes scoped: GET /api/customers/:id, GET /api/customers/:id/cards, GET /api/customers/:id/wallets now verify the customer belongs to the caller's org/BU",
          "Wallet routes scoped: GET /api/wallet/:id/transactions, GET /api/wallet/:id/cards, GET /api/wallets/:customerId, GET /api/wallets/:customerId/transactions — traces wallet → user for org membership",
          "Card routes scoped: GET /api/cards/:id/detail, GET /api/cards/:id, GET /api/cards/:id/transactions — traces card → user for org membership",
          "Top-up route scoped: GET /api/topups/:id — traces top-up → user for org membership",
          "Support ticket route scoped: GET /api/support/tickets/:id — traces ticket → user for org membership",
          "Inventory routes hardened: GET /api/inventory/cards and GET /api/inventory/shipping now require SUPER/FINANCE role or org-level scoping with 403 fallback",
          "New `getShippingRecordsByOrganization()` storage method with JOIN through business_units table for org-level inventory filtering",
        ],
      },
      {
        type: "added",
        category: "Unified Admin Transactions Page",
        items: [
          "Transactions page now fetches from 3 parallel sources: card transactions (GET /api/reports/transactions), wallet transactions (GET /api/wallet-transactions), and PayHub top-ups (GET /api/topups)",
          "New GET /api/wallet-transactions route with full org/BU scoping for admin wallet transaction listing",
          "New `getAllWalletTransactions()` storage method with double JOIN (wallet_transactions → wallets → users) for org-scoped queries",
          "All 3 sources normalized into a common `UnifiedTransaction` shape with id, source, date, amountCents, currency, status, type, description, and reference fields",
          "Color-coded source badges: Card (blue), Wallet (green), Top-up (purple)",
          "Source dropdown filter to show All / Card / Wallet / Top-up transactions",
          "Expanded status filter covering all statuses across types: SETTLED, SUCCESS, PENDING, AUTHORIZED, DECLINED, FAILED, REVERSED, REFUNDED, EXPIRED",
          "4 stats cards showing aggregated totals across all transaction sources: Total Transactions, Total Volume, Successful, and Pending",
        ],
      },
      {
        type: "fixed",
        category: "Dashboard KPI Accuracy",
        items: [
          "\"Amount Funded\" now sums from `fund_loads` table (WHERE status=COMPLETED) instead of current wallet balances — shows true historical funding total, not just current balance",
          "\"Total Transactions\" now includes both card transaction count and wallet transaction count (was card-only)",
          "\"Total Volume\" now includes completed fund load amounts in addition to card transaction volume (was card-only)",
          "All 9 KPI queries properly scoped to org/BU when user has limited access using a reusable `userScopeCondition` variable",
          "Wallet transaction count query uses double JOIN (wallet_transactions → wallets → users) for correct org-scoped counting",
          "Fund load sum query JOINs through users table with status=COMPLETED filter for accurate historical funding data",
        ],
      },
      {
        type: "added",
        category: "Sentry Error Tracking",
        items: [
          "Sentry SDK initialized on both client (browser) and server (Node.js) applications for production error monitoring",
          "Updated Sentry organization slug and DSN configuration in Vite config",
          "Source map upload enabled via Sentry Vite plugin for accurate error stack traces",
        ],
      },
      {
        type: "added",
        category: "Login Page & Authentication",
        items: [
          "New login page with authentication form and responsive hero section",
          "Updated client routing and app sidebar for authenticated navigation flow",
          "Security middleware updates for Cross-Origin-Opener-Policy headers",
        ],
      },
      {
        type: "added",
        category: "Storage & API Infrastructure",
        items: [
          "Synced virtual card migration (0006_insert_synced_virtual_card.sql) for Central API card data",
          "Core payment, wallet, card, and customer management API namespace updates with improved error handling",
          "GSX tenant admin creation script improvements with better defaults",
          "Sync service enhancements for bidirectional data flow reliability",
        ],
      },
      {
        type: "improved",
        category: "Demo Data Removal",
        items: [
          "Removed hardcoded mock timeline events from Activity Tab — now shows only real audit data from the API with live polling",
          "Removed demo QR mode from Wallet Top-Up Dialog — payment flow now requires real PayHub integration (no more simulated payments)",
          "Removed fake trend percentages and placeholder data from Report Center — charts now display only real data",
        ],
      },
    ],
  },
  {
    version: "v1.0.0-rc.1",
    date: "March 9, 2026",
    title: "Sync Duplicate Resolution & Central API ID Mapping (Release Candidate 1)",
    description: "RC1 — Fixes a critical sync defect where incoming events from the Central API created duplicate customer records instead of updating existing ones. Adds cross-system ID mapping via centralApiId, a 3-tier customer resolution strategy (direct ID, centralApiId, phone match), and a one-time data migration to clean up the affected TUSHANT CHAKRABORTY record.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Central API ID Cross-Reference",
        items: [
          "New `central_api_id` column on users table — stores the Central API entity UUID for cross-system resolution",
          "Unique constraint on central_api_id to prevent accidental duplicate mappings",
          "Partial index on central_api_id (WHERE NOT NULL) for fast lookup during sync ingestion",
          "Migration 0004_add_central_api_id.sql for schema change",
        ],
      },
      {
        type: "added",
        category: "Customer Sync Resolution Strategy",
        items: [
          "New `findCustomerByCentralApiId()` storage method — looks up customer by their Central API entity ID",
          "New `findCustomerByPhone()` storage method — matches customer by phone number within an organization scope",
          "3-tier `resolveLocalCustomer()` function in applySyncChange: (1) direct local ID match, (2) centralApiId match, (3) phone + org fallback",
          "On successful resolution, centralApiId is stamped on the matched record so future syncs resolve instantly",
          "New records created via sync now include centralApiId from the start",
          "Both IStorage interface and MockStorage class updated with new lookup methods",
        ],
      },
      {
        type: "fixed",
        category: "Sync Duplicate Prevention",
        items: [
          "Fixed root cause: incoming customer.updated events with unrecognized Central API entity IDs no longer silently fail or create duplicates",
          "customer.created and customer.updated actions now share the same resolution logic — both check for existing records before inserting",
          "customer.deleted action now resolves the correct local record via the same 3-tier strategy before deleting",
        ],
      },
      {
        type: "fixed",
        category: "Data Fix: TUSHANT CHAKRABORTY Duplicate",
        items: [
          "Migration 0005_fix_tushant_duplicate.sql deletes the orphaned duplicate record (88e390bd) created by the failed sync",
          "Updates the original record (789020c1) with correct Central API data: name TUSHANT CHAKRABORTY, KYC VERIFIED, tier 3, PAN CEYPC5124H, DOB 1958-03-29",
          "Stamps central_api_id = 77e52bef on the original record so future syncs from Central API resolve correctly",
          "Sets organization_id to the correct org (b1e0b223) and status to ACTIVE",
        ],
      },
      {
        type: "security",
        category: "Sync Integrity",
        items: [
          "Phone-based fallback matching is scoped to the same organization to prevent cross-tenant record hijacking",
          "centralApiId uniqueness constraint prevents two local records from claiming the same Central API identity",
          "Sync-loop prevention (_syncApplyInProgress flag) continues to guard against re-emission of inbound changes",
        ],
      },
    ],
  },
  {
    version: "v0.11.0",
    date: "February 27, 2026",
    title: "Bidirectional Sync Engine & GSX Flow",
    description: "Real-time bidirectional sync between VL-VC and Central API with HMAC-SHA256 signed webhooks, idempotent event ledger, and exponential-backoff retries. GSX USDT-to-Virtual-Card integration flow documented end-to-end. Dashboard expanded with wallet funding KPIs.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Bidirectional Sync Service",
        items: [
          "Full sync-service.ts module handling outgoing event emission and incoming event processing",
          "HMAC-SHA256 webhook signing with timestamp-based replay protection (5-minute window)",
          "Timing-safe signature comparison to prevent timing attacks",
          "Idempotent event processing via sync_ledger table — duplicate events rejected automatically",
          "Exponential-backoff retry schedule: immediate → 1 min → 5 min → 30 min → 2 hours (5 attempts max)",
          "Background sync worker with configurable polling interval (default 30s)",
          "Sync health endpoint reporting pending, failed, and delivered event counts",
          "Central API treated as source of truth for conflict resolution",
        ],
      },
      {
        type: "added",
        category: "Sync Database Schema (3 New Tables)",
        items: [
          "sync_config — remote system registration with webhook URL, API secret, active flag, and heartbeat tracking",
          "sync_events — outgoing event queue with event type, entity type/ID, payload, status, retry tracking, and delivery timestamp",
          "sync_ledger — incoming event deduplication ledger with event ID, entity reference, origin system, and payload hash",
          "sync_event_status enum: pending, delivered, failed, expired",
          "sync_action enum: created, updated, deleted",
          "Zod insert schemas and TypeScript types exported for all three tables",
        ],
      },
      {
        type: "added",
        category: "Sync API Endpoints (3 New Routes)",
        items: [
          "POST /api/sync/incoming — receives webhook events from Central API with signature verification",
          "GET /api/sync/health — returns sync status: enabled flag, remote config, pending/failed/delivered counts",
          "POST /api/sync/retry — manually triggers retry of pending sync events in the queue",
        ],
      },
      {
        type: "added",
        category: "Storage Layer Sync Integration",
        items: [
          "All CRUD operations across 7 entity types now emit sync events on create, update, and delete",
          "Synced entities: organization, business_unit, product, customer, wallet, virtual_card, admin",
          "Sync-loop prevention: _syncApplyInProgress flag prevents re-emission of incoming changes",
          "applySyncChange() method on IStorage interface for applying remote changes to local database",
          "Field mapping between Central API and VL-VC schemas (customerId → userId, name → firstName/lastName, password → passwordHash)",
          "Admin sync bypasses bcrypt re-hashing — pre-hashed passwords written directly",
        ],
      },
      {
        type: "added",
        category: "GSX USDT-to-Virtual-Card Flow",
        items: [
          "Complete GSX_FLOW_DIAGRAM.md documenting the USDT sell → KYC → card issuance pipeline",
          "Mermaid flowchart with 5 subgraphs: B2B Funding, Customer Entry, New Customer KYC, Existing Customer Fast-Track, Post-Issuance",
          "Color-coded flow diagram rendered as PNG (light + dark variants)",
          "B2B funding: GSX bulk transfer → VL 1% fee deduction → iServeU Program Manager top-up",
          "Path A (new customer): Signzy/Digilocker KYC → collect name/phone/address/USDT amount → iServeU card issuance",
          "Path B (existing customer): whitelabel fast-track → iServeU card generation/load",
          "Post-issuance tracking: request status logging, email timestamps, GSX Org Panel float visibility",
          "Open technical asks documented: PayHub API changes, customer login redirect, GSX dashboard requirements",
        ],
      },
      {
        type: "added",
        category: "Dashboard Funding KPIs",
        items: [
          "Amount Funded KPI card — total balance across all active wallets with currency formatting",
          "Remaining Balance KPI card — total available balance across all active wallets",
          "Dedicated 2-column funding row with emerald/teal colour theming and wallet/piggybank icons",
          "DashboardKPIs interface extended with amountFundedCents and remainingBalanceCents fields",
          "Server-side aggregation: parallel SUM queries on wallets.balance_cents and wallets.available_balance_cents",
        ],
      },
      {
        type: "improved",
        category: "Dashboard KPI Display",
        items: [
          "KPI cards now use display() formatter function for locale-aware number rendering",
          "Consistent toLocaleString() formatting for Customers, Pending KYC, and Open Tickets",
          "Funding KPIs use organization-level currency formatting via OrgSettingsProvider context",
        ],
      },
      {
        type: "improved",
        category: "Changelog & Documentation",
        items: [
          "Changelog page refactored with collapsible change-type sections and summary chip counts",
          "v0.10.0 entry expanded with full Partner API Developer Portal details",
          "Docs page minor layout refinement for tab navigation",
        ],
      },
      {
        type: "security",
        category: "Sync Security",
        items: [
          "HMAC-SHA256 webhook signatures on all outgoing sync events",
          "Inbound signature verification with timing-safe comparison (crypto.timingSafeEqual)",
          "5-minute replay protection window — events with stale timestamps rejected",
          "Sync secret stored in sync_config table, never exposed in API responses",
          "Sync-loop prevention ensures inbound changes are not re-emitted as outbound events",
        ],
      },
      {
        type: "fixed",
        category: "Storage & Query Fixes",
        items: [
          "Fixed SQL indentation inconsistencies in virtual card and wallet JOIN queries",
          "MockStorage updated to implement applySyncChange interface for test compatibility",
          "Mock getDashboardKPIs now includes amountFundedCents and remainingBalanceCents aggregation",
        ],
      },
    ],
  },
  {
    version: "v0.10.0",
    date: "February 23, 2026",
    title: "Partner API Developer Portal",
    description: "Full-featured developer portal for managing Partner API access — API keys, webhooks, usage analytics, security testing, and interactive documentation. Includes 22 new server routes proxying to the Central API, 5 new pages, and critical product scoping fixes.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Server: 22 Partner API Proxy Routes",
        items: [
          "7 API key routes — create, list, detail, update, revoke, rotate, usage logs",
          "4 webhook routes — create, list, update, delete",
          "3 analytics routes — stats, daily usage breakdown, webhook event history",
          "8 security test endpoints with real implementations against the Central API",
          "Automatic organization scoping on all requests via session headers",
          "All routes restricted to Super Admin and Organization Admin roles",
        ],
      },
      {
        type: "added",
        category: "Server: 8 Security Test Suites",
        items: [
          "Authentication — verifies session, API config, and active cookie",
          "Scope enforcement — creates key with limited scopes, validates assignment",
          "Rate limiting — configures limits, verifies, updates",
          "Idempotency — UUID generation, deterministic hashing",
          "Webhook signing — HMAC-SHA256 sign/verify, tamper detection, timing-safe comparison",
          "IP allowlist — add, update, and clear IP restrictions",
          "Key lifecycle — full create → read → update → rotate → revoke flow",
          "End-to-end — auth, key CRUD, webhook CRUD, signing, analytics in one suite",
          "All tests auto-clean temporary resources after execution",
        ],
      },
      {
        type: "added",
        category: "Page: API Keys",
        items: [
          "CRUD table with key prefix, environment, status, scopes, rate limits, and timestamps",
          "Create dialog with environment, scopes, rate limits, IP allowlist, and expiry options",
          "One-time key display after create/rotate — copy button with 'shown only once' warning",
          "Row actions: view details, edit, rotate (with confirm), revoke (with confirm)",
          "Expandable detail drawer with paginated usage logs",
          "Only active keys displayed — revoked and expired keys filtered out",
        ],
      },
      {
        type: "added",
        category: "Page: Webhooks",
        items: [
          "CRUD table with URL, subscribed events, status, failure count, and last delivery",
          "Create dialog with HTTPS URL validation and event selection",
          "One-time secret display after creation with copy button and warning",
          "Edit, delete, and re-enable actions per webhook",
          "Events: card.created, card.updated, card.frozen, card.unfrozen, card.cancelled",
        ],
      },
      {
        type: "added",
        category: "Page: Usage Analytics",
        items: [
          "5 KPI cards: Active Keys, Total Requests, Avg Latency, Active Webhooks, Delivery Rate",
          "Gradient area chart showing daily request volume and errors over 30 days",
          "Top endpoints table with colour-coded method badges",
          "Per-key usage breakdown table",
          "Recent webhook events table with status, attempts, and response codes",
        ],
      },
      {
        type: "added",
        category: "Page: Security Tests",
        items: [
          "Grid of 8 test cards with unique icons and colour themes",
          "Run All button with sequential execution and progress tracking",
          "Per-test run button with loading state",
          "Summary bar with passed/failed/remaining counts",
          "Expandable sub-results showing individual check outcomes",
        ],
      },
      {
        type: "added",
        category: "Page: API Documentation",
        items: [
          "Left sidebar navigation with 6 sections synced to URL",
          "Overview — base URL, auth method, rate limits, quick start guide, scopes reference",
          "Authentication — code examples in cURL, Node.js, and Python with copy button",
          "Cards API — issue card, get details, freeze/unfreeze/cancel/balance endpoints",
          "Webhooks — event types, payload examples, HMAC signature verification code",
          "Errors & Limits — status codes, error format, rate limit headers, idempotency keys",
          "Security — 6 best practice cards, IP allowlist configuration example",
        ],
      },
      {
        type: "added",
        category: "Developer Sidebar Navigation",
        items: [
          "New 'Developer' section in sidebar with 5 nav items",
          "Pages: API Keys, Webhooks, API Usage, Security Tests, API Docs",
          "Visible to Super Admin and Organization Admin roles only",
        ],
      },
      {
        type: "added",
        category: "Central API Client Enhancements",
        items: [
          "Session request method now public for use by proxy routes",
          "Extra headers support for organization scoping",
          "Automatic org ID forwarding from session to Central API",
        ],
      },
      {
        type: "improved",
        category: "Usage Analytics Chart",
        items: [
          "Replaced bar chart with smooth gradient area chart",
          "Translucent gradient fills for requests and errors",
          "Monotone curve interpolation for cleaner data visualization",
        ],
      },
      {
        type: "improved",
        category: "API Docs Layout",
        items: [
          "Upgraded from horizontal tabs to left sidebar navigation",
          "Sidebar with icons and active state highlighting",
          "Sections split into separate components for maintainability",
          "Scrollable content area with URL-synced tab state",
        ],
      },
      {
        type: "fixed",
        category: "Product Data Scoping",
        items: [
          "Products endpoint now respects organization scope — previously returned all products to all users",
          "Cascading scope: session org → resolve from BU → Super/Finance see all → deny",
          "Product detail endpoint validates org ownership — prevents cross-org access by ID",
          "Organization detail panel now filters by org — no longer shows all 4 products for every org",
        ],
      },
      {
        type: "fixed",
        category: "Security Test Endpoints",
        items: [
          "Replaced 8 broken proxy routes with real server-side test implementations",
          "Tests create temporary resources and clean up automatically",
          "Crypto tests (idempotency, signing) run locally without API calls",
        ],
      },
      {
        type: "fixed",
        category: "API Keys Display",
        items: [
          "Table now only shows active keys — revoked and expired keys hidden",
          "Empty state correctly displays when all keys are revoked",
        ],
      },
      {
        type: "added",
        category: "Page Access Levels Matrix",
        items: [
          "Full access control reference table in Docs → Overview (Super Admin only)",
          "Color-coded role badges (S/O/B/F) for every page in the platform",
          "Grouped by section: Core, Organization, Card Operations, Operations, Developer, Platform",
          "Notes on filtered access where applicable (e.g. Docs tab filtering for org admins)",
        ],
      },
      {
        type: "security",
        category: "Partner API Access Control",
        items: [
          "All 22 proxy routes restricted to Super Admin and Organization Admin roles",
          "API keys and webhooks automatically scoped to the admin's organization",
          "Product endpoints validate organization ownership — no cross-org data leakage",
          "Test resources prefixed and cleaned up to prevent data accumulation",
          "API keys and webhook secrets displayed only once with explicit copy-to-clipboard",
          "Integrations page restricted to Super Admin only — org admins no longer have access",
          "Platform Docs filtered by role — org admins see only Products, Customers, Wallets, Cards tabs",
          "URL-based tab access prevented — restricted tabs fall back to first visible tab",
        ],
      },
    ],
  },
  {
    version: "v0.9.0",
    date: "February 18, 2026",
    title: "KYC Verification, Card Designs & Regional Settings",
    description: "Video KYC via Signzy with real-time status polling, Aadhaar eKYC with OTP verification, 9 branded prepaid card design variants with picker UI, organization-level country/currency settings replacing all hardcoded formatters, Sentry error monitoring, automated demo video pipeline, and physical card removal in favour of virtual-only.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Video KYC (Signzy Integration)",
        items: [
          "VideoKycDialog component with embedded Signzy iframe for document capture and face liveness verification",
          "3-step journey flow: Start KYC → In-Progress (iframe/demo video tabs) → Complete with extracted data display",
          "Real-time status polling every 5 seconds via GET /api/kyc/status/:journeyId",
          "KYC results display: extracted name, date of birth, liveness score percentage, and face match percentage",
          "Fallback 'Open in New Tab' button after 5-second iframe timeout for browser compatibility",
          "Journey states: idle, starting, in-progress, completed, failed, abandoned — each with dedicated UI",
          "Admin KYC management: approve/reject endpoints with tier assignment and rejection reasons",
          "POST /api/kyc/start, GET /api/kyc/status/:journeyId, GET /api/kyc/results/:journeyId endpoints",
          "POST /api/kyc/:customerId/approve and POST /api/kyc/:customerId/reject admin endpoints",
        ],
      },
      {
        type: "added",
        category: "Aadhaar eKYC (India)",
        items: [
          "AadhaarKycDialog component with 4-step form flow: Consent → Aadhaar Number → OTP Verification → Complete",
          "Auto-formatted Aadhaar input with XXXX XXXX XXXX masking and 12-digit validation",
          "6-digit OTP verification against Aadhaar-linked mobile number with resend capability",
          "Verified data display: name, date of birth, gender from UIDAI response",
          "POST /api/india/kyc/aadhaar/initiate and POST /api/india/kyc/aadhaar/verify-otp endpoints",
          "On verification success: updates customer kycStatus to VERIFIED, sets kycTier to 2, syncs firstName/lastName from Aadhaar data",
          "UIDAI consent step with mandatory checkbox and data-access disclosure",
        ],
      },
      {
        type: "added",
        category: "Card Design System (9 Variants)",
        items: [
          "9 branded prepaid card design variants with unique front and back PNG artwork",
          "card-designs.ts module: CardVariant type (1-9), getCardDesign(), getVariantFromIndex(), getVariantFromId() helpers",
          "Card design picker UI in both Issue Card (admin) and Request Card (customer) dialogs — 3x3 image grid with selection checkmark",
          "cardDesign column added to virtual_cards database table with server-side validation (1-9 range)",
          "CardVisual (customer portal) refactored: front shows card image + network logo (VISA/Mastercard/RuPay), back shows card number, holder name, expiry, and CVV overlaid on card image",
          "VirtualCardVisual (admin) refactored: removed hardcoded EMV chip and contactless icon in favour of baked-in card artwork, improved text drop-shadows for readability",
          "Consistent card brand rendering: VISA (italic text), Mastercard (overlapping circles), RuPay (bold text)",
          "Card design persisted per card and rendered consistently across portal cards list, card detail, and admin card drawer",
        ],
      },
      {
        type: "added",
        category: "Organization Regional Settings",
        items: [
          "BaseCountry (AU, NZ, IN, GB, US) and BaseCurrency (AUD, NZD, INR, GBP, EUR, USD) types with country-currency mapping constants",
          "OrgSettingsProvider React context with formatCurrency() helper using Intl.NumberFormat with locale-aware formatting",
          "Country and currency dropdown selectors on Create Organization dialog and Organization Detail page with auto-currency-from-country selection",
          "Regional Settings card on organization detail and drawer sidebar views",
          "GET /api/organization/settings (admin) and GET /api/customer/organization/settings (portal) endpoints",
          "Deep-merge settings in PATCH /api/organizations/:id to preserve existing settings keys",
          "Replaced all hardcoded currency formatters (formatINR, local formatCurrency) across 20+ files with context-based formatCurrency",
        ],
      },
      {
        type: "added",
        category: "Sentry Error Monitoring",
        items: [
          "@sentry/node integration on server with PII collection enabled",
          "@sentry/react integration on client, initialized before React render for comprehensive coverage",
          "Sentry Vite plugin for automatic source map upload with separate frontend and backend project DSNs",
          "Source map generation enabled in production builds for accurate error stack traces",
        ],
      },
      {
        type: "added",
        category: "Automated Demo Video Pipeline",
        items: [
          "Playwright-based automated UI capture system: navigates live app, captures 1920x1080 screenshots with highlight region metadata",
          "16 flow scripts covering admin and portal journeys: login, customer onboard, integrations, KYC, org/products, reports, settings, support, cards, wallet, full journey",
          "B2B master flow (39 steps) and B2C master flow covering complete platform walkthroughs",
          "Remotion video composition engine: intro slide, per-step screenshot display with annotations, step indicator, outro — all at 30fps",
          "5 reusable video components: ScreenFrame (macOS-style browser), Annotation (highlight boxes), StepIndicator, AnimatedText, Background",
          "14 registered video compositions with per-flow accent colours and dynamic duration calculation",
          "Admin impersonation-based portal access for automated captures without real OTP",
          "CaptureSession utility class for structured screenshot collection and metadata.json output",
        ],
      },
      {
        type: "added",
        category: "PayPrism API Client",
        items: [
          "Comprehensive PayPrism client service with session-based admin auth (auto-retry on 401) and API key auth",
          "Payment methods: createPayinIntent (UPI QR), getPayinStatus, getPayinBalance, getPayoutStatus, getPayoutBalance",
          "KYC methods: startKyc, getKycStatus, getKycResults, getCustomerKycStatus, approveKyc, rejectKyc",
          "Customer OTP auth: requestOtp, verifyOtp (public endpoints, no API key required)",
          "Dedicated KYC_API_KEY environment variable for Signzy-specific endpoints",
          "Singleton pattern via getPayPrismClient() with configurable baseUrl, timeout, and credentials",
        ],
      },
      {
        type: "added",
        category: "Feature Flags & Infrastructure",
        items: [
          "Feature flags context with localStorage persistence (cardvault-feature-flags) — currently gates Inventory nav item",
          "Stripe service layer (stripe-service.ts) for payment processing integration",
          "Database migration 0001_lovely_killraven with comprehensive schema additions for KYC, payments, and regional settings",
          "Customer profile update endpoint: PATCH /api/customer/profile with firstName, lastName, email support",
          "Customer SQL fix scripts for data mapping corrections (scripts/fix-customer-mapping.sql)",
        ],
      },
      {
        type: "added",
        category: "E2E Flow Documentation",
        items: [
          "New 'E2E Flows' section in Docs page with interactive Mermaid diagrams",
          "KYC Verification flow diagram: login → Signzy journey → document capture → liveness → verified",
          "QR Code Wallet Top-Up flow diagram: amount selection → QR generation → UPI payment → polling → wallet credited",
          "Complete Onboarding-to-Transaction journey diagram: onboarding → KYC → funding → card usage",
          "System Architecture sequence diagram showing Customer Portal ↔ Server ↔ Signzy ↔ PayHub ↔ UPI interactions",
        ],
      },
      {
        type: "improved",
        category: "Customer Portal UX",
        items: [
          "Portal account page: functional profile editing with save/cancel, loading state, and PATCH /api/customer/profile integration",
          "Customer sidebar: VedaLogic SVG logo replaces text-based branding, animated show/hide on collapse with proper header clearance",
          "Customer header: removed redundant center logo for cleaner layout",
          "Wallet top-up dialog: demo mode with placeholder QR image and 'Simulate Payment' button for presentations",
          "Card detail page: passes card holder name from customer context and uses persisted card design variant",
        ],
      },
      {
        type: "improved",
        category: "Admin Sidebar & Navigation",
        items: [
          "Role-based sidebar navigation with 5 sections: Dashboard, Card Operations, Operations, Settings, More (collapsible)",
          "Feature flag gating for Inventory nav item (hidden by default via feature flags context)",
          "Avatar picker refactored with simplified preset library and cleaner component structure",
          "Signzy integration bug fixes and PayPrism client session handling improvements",
        ],
      },
      {
        type: "improved",
        category: "Card Visuals & Rendering",
        items: [
          "Card front: branding, EMV chip, and contactless icon now baked into card design artwork — component renders only network logo and status badge as overlays",
          "Card back: full card details (masked number, holder, expiry, CVV) overlaid on back artwork with enhanced drop-shadow readability",
          "Status handling expanded from FROZEN-only to FROZEN, CANCELLED, EXPIRED with distinct colour-coded badges",
          "Frosted overlay reduced from bg-white/20 to bg-white/10 for subtler inactive-card treatment",
          "Text drop-shadows upgraded to rgba(0,0,0,0.8) for improved legibility over all 9 card designs",
          "Responsive text scaling: sm/md/lg size variants with proportional number, label, detail, and brand text sizes",
        ],
      },
      {
        type: "fixed",
        category: "Bug Fixes",
        items: [
          "Fixed menu access level visibility — role-based nav items now correctly filtered per user role",
          "Fixed entity drawer overlap bug causing z-index conflicts with sidebar",
          "Fixed entity data relationship mapping for customer-to-business-unit scoping",
          "Fixed customer data mapping: SUPER/FINANCE/ORG admins can now filter customers by business unit via query param",
          "Fixed stray closing bracket in routes.ts that broke esbuild production builds",
          "Fixed TypeScript errors: JourneyUpdate, ReferenceType, and Aadhaar type definitions corrected",
          "Fixed avatar picker targeting wrong settings file (settings/profile.tsx vs settings/index.tsx)",
          "Removed physical card virtual-to-physical conversion page (virtual-only platform)",
        ],
      },
      {
        type: "security",
        category: "KYC & Deployment Security",
        items: [
          "KYC endpoints protected with requireAnyAuth middleware (supports both admin and customer sessions)",
          "Admin KYC approve/reject restricted to SUPER, ORGANIZATION, BUSINESS_UNIT roles",
          "Aadhaar number encrypted at rest (aadhaarNumberEncrypted field) and listed in sensitive fields sanitisation",
          "Sentry PII collection enabled for both client and server for complete error reproduction context",
          "Cross-Origin-Opener-Policy: same-origin header applied to all API routes via vercel.json",
          "Card design server-side validation: values clamped to 1-9 range, defaults to 1 for invalid input",
        ],
      },
      {
        type: "design",
        category: "Card Design Gallery",
        items: [
          "9 unique prepaid card artwork variations with distinct colour themes and patterns",
          "3x3 responsive grid picker with ring-2 selection indicator and checkmark badge",
          "Opacity transition: 60% for unselected designs, 100% on hover, full opacity when selected",
          "Consistent aspect-ratio (1.6:1) thumbnails matching real card proportions",
        ],
      },
      {
        type: "design",
        category: "Portal & Admin Visual Updates",
        items: [
          "VedaLogic SVG logo in customer sidebar with light/dark mode invert support",
          "Cleaner customer header without duplicate branding",
          "Enhanced KYC dialog with step indicators, animated spinners, and colour-coded journey states",
          "E2E flow documentation with colour-coded Mermaid diagrams and step-by-step visual breakdowns",
        ],
      },
    ],
  },
  {
    version: "v0.8.0",
    date: "February 8, 2026",
    title: "Profile Customization & Performance Optimization",
    description: "Avatar system with preset gallery and custom uploads via Vercel Blob, real-time profile editing, database performance optimization with composite queries and indexes, enhanced documentation system with compliance guides.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Avatar System",
        items: [
          "Avatar Picker component with 50+ preset avatars organized by gender and color",
          "Custom avatar upload with Vercel Blob storage integration (5MB max)",
          "Real-time avatar preview and filtering by gender/color preferences",
          "Avatar presets library with categorization (male, female, neutral; multiple colors)",
          "Drag-and-drop image upload with file validation (PNG, JPG, GIF)",
          "Avatar API endpoints for upload and user profile updates",
        ],
      },
      {
        type: "added",
        category: "Profile Management",
        items: [
          "Real-time profile editing with live session updates",
          "Inline profile field editing (first name, last name, email)",
          "Profile API with validation and authentication",
          "Automatic session synchronization after profile changes",
          "User-friendly profile page with improved layout and spacing",
        ],
      },
      {
        type: "added",
        category: "Documentation System",
        items: [
          "Enhanced Docs page with collapsible sidebar navigation",
          "Compliance documentation suite (Executive Summary, Legal, Technical, Roadmap)",
          "Markdown reader component for dynamic documentation rendering",
          "Mermaid diagram support for interactive flowcharts and entity relationships",
          "Use cases documentation (B2B Corporate vs B2C Consumer comparison)",
          "Master diagrams for business hierarchy visualization",
        ],
      },
      {
        type: "added",
        category: "Database Performance",
        items: [
          "Composite database indexes for faster customer, card, and wallet queries",
          "Performance-optimized query patterns with JOIN operations",
          "Migration script for adding strategic indexes (customers, cards, wallets, transactions)",
          "Query execution time improvements (estimated 3-10x faster for complex queries)",
          "Database diagnostics script for wallet analysis",
        ],
      },
      {
        type: "added",
        category: "Queue System",
        items: [
          "Comprehensive queue management system for async operations",
          "Queue executor with context management and state tracking",
          "Queue generator for task automation",
          "Queue auto-executor for scheduled job processing",
          "Queue system documentation with usage examples",
        ],
      },
      {
        type: "added",
        category: "PayPrism Integration",
        items: [
          "PayPrism client service for payment processing",
          "PayPrism integration documentation and configuration guides",
          "PayPrism API layer deployment scripts",
          "Complete PayPrism integration final documentation",
        ],
      },
      {
        type: "improved",
        category: "API Performance",
        items: [
          "Refactored customer detail endpoint to use composite queries (fewer DB calls)",
          "Optimized card detail endpoint with JOIN-based queries",
          "Enhanced wallet detail endpoint for faster balance retrieval",
          "Reduced N+1 query problems across customer, card, and wallet endpoints",
          "Improved response times for entity detail pages",
        ],
      },
      {
        type: "improved",
        category: "Settings Page",
        items: [
          "Profile tab with avatar customization and real-time editing",
          "Improved visual layout with better spacing and hierarchy",
          "Enhanced user experience for profile updates",
          "Better error handling and success feedback",
          "Consistent design language across all settings tabs",
        ],
      },
      {
        type: "improved",
        category: "Sidebar Navigation",
        items: [
          "Refined sidebar UI with better icon spacing",
          "Improved collapse/expand animations",
          "Enhanced tooltip positioning and timing",
          "Better visual feedback for active states",
          "Optimized sidebar scrolling performance",
        ],
      },
      {
        type: "improved",
        category: "Activity & Audit",
        items: [
          "Enhanced activity tab with better event grouping",
          "Improved audit log filtering and search",
          "Better formatting for audit entries",
          "Clearer display of activity timestamps",
          "Enhanced audit log pagination",
        ],
      },
      {
        type: "design",
        category: "Avatar Gallery",
        items: [
          "4-column responsive grid for preset avatars",
          "Hover effects with border highlighting",
          "Selection indicators with checkmark badges",
          "Smooth transitions between avatar states",
          "Accessible color swatches for filtering",
        ],
      },
      {
        type: "design",
        category: "Profile UI",
        items: [
          "Cleaner profile card layout with improved spacing",
          "Better visual hierarchy for profile information",
          "Inline editing indicators for editable fields",
          "Improved typography and text sizing",
          "Consistent button styling across profile actions",
        ],
      },
      {
        type: "security",
        category: "Upload Security",
        items: [
          "File type validation for avatar uploads (images only)",
          "File size validation (5MB maximum)",
          "Secure blob storage with Vercel Blob integration",
          "Authenticated upload endpoints with session validation",
          "Content-Type verification for uploaded files",
        ],
      },
      {
        type: "fixed",
        category: "Bug Fixes",
        items: [
          "Fixed avatar picker state management in settings",
          "Resolved profile update race conditions",
          "Fixed session synchronization after profile changes",
          "Corrected database query performance issues",
          "Fixed navigation state in docs page",
        ],
      },
    ],
  },
  {
    version: "v0.7.0",
    date: "February 7, 2026",
    title: "Entity Renaming & Audit System",
    description: "Major platform-wide entity renaming, comprehensive audit logging system, database schema simplification, and extensive testing documentation.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Audit System",
        items: [
          "Comprehensive audit logging service (audit.ts) for tracking all platform changes",
          "Support for standard audit actions: CREATE, UPDATE, DELETE, APPROVE, REJECT, STATUS_CHANGE",
          "Authentication event tracking: LOGIN, LOGOUT, PASSWORD_RESET, PERMISSION_CHANGE",
          "Detailed audit records with actor information, old/new values, IP address, and user agent",
          "Audit logs for all resource types: organizations, organization admins, products, customers, cards, wallets, transactions",
        ],
      },
      {
        type: "added",
        category: "Testing Documentation",
        items: [
          "End-to-End Testing Flows guide (E2E-TESTING-FLOWS.md) with complete user journey scenarios",
          "QA Testing Sheet (QAT-TESTING-SHEET.md) with comprehensive test cases and validation steps",
          "Detailed testing scenarios for all entity types and user roles",
          "Test data and expected outcomes for quality assurance",
        ],
      },
      {
        type: "improved",
        category: "Entity Naming (Breaking Change)",
        items: [
          "Renamed 'Program Manager' to 'Organization' across entire platform",
          "Renamed 'Corporate Admin' to 'Organization Admin' across entire platform",
          "Updated all UI components, forms, and dialogs with new terminology",
          "Updated all API endpoints and route handlers with new entity names",
          "Updated documentation and help text to reflect new naming conventions",
          "Improved clarity and consistency of entity hierarchy",
        ],
      },
      {
        type: "improved",
        category: "Database Schema",
        items: [
          "Removed redundant 'request_status' column from organizations table",
          "Removed redundant 'request_status' column from organization_admins table",
          "Simplified status management to use single 'status' column with default 'ACTIVE'",
          "Database migration with verification queries to ensure clean removal",
          "Streamlined entity lifecycle management",
        ],
      },
      {
        type: "improved",
        category: "Code Simplification",
        items: [
          "Removed drafted status handling from organization workflows",
          "Simplified organization and organization admin creation forms",
          "Streamlined view and detail pages with cleaner code structure",
          "Refactored route handlers for better maintainability",
          "Optimized storage layer with reduced complexity",
        ],
      },
      {
        type: "improved",
        category: "Activity Tab Enhancement",
        items: [
          "Enhanced activity timeline with better event grouping",
          "Improved status change visualization",
          "Better formatting for audit log entries",
          "Clearer display of actor information and timestamps",
        ],
      },
      {
        type: "fixed",
        category: "File Organization",
        items: [
          "Renamed dialog files to match new entity naming (create-organization.tsx, create-organization-admin.tsx)",
          "Reorganized page directories from /program-manager to /organization",
          "Updated all import paths and route references",
          "Fixed component exports in index files",
        ],
      },
    ],
  },
  {
    version: "v0.6.0",
    date: "February 4, 2026",
    title: "Card Flip Animation & Branding Updates",
    description: "Interactive card flip animation on hover, VedaLogic Consulting branding across the platform, and enhanced card details page.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Card Flip Animation",
        items: [
          "Interactive 3D card flip animation triggered on hover",
          "Front displays VedaLogic branded card design (vedalogic-card-2.svg)",
          "Back displays alternate card design (vedalogic-card-1.svg)",
          "Smooth 0.6s flip transition with CSS 3D transforms",
          "Backface visibility handling for clean flip effect",
          "Frozen card state support with grayscale overlay",
        ],
      },
      {
        type: "added",
        category: "Card Details Page Enhancement",
        items: [
          "CardVisual component added to /portal/cards/:id detail page",
          "Interactive flip animation on card detail view",
          "'Hover over the card to see the back' instruction text",
          "Responsive layout with card visual alongside card information",
          "Card number, expiry, and brand details displayed beside visual",
        ],
      },
      {
        type: "added",
        category: "VedaLogic Consulting Branding",
        items: [
          "'Powered by VedaLogic Consulting' added to admin sidebar footer",
          "'Powered by VedaLogic Consulting' added to customer portal sidebar footer",
          "'Powered by VedaLogic Consulting' added to admin login page (top left)",
          "'Powered by VedaLogic Consulting' added to customer portal login page (top left)",
          "Consistent branding placement across all portal areas",
        ],
      },
      {
        type: "improved",
        category: "CardVisual Component",
        items: [
          "Simplified component to use SVG image backgrounds directly",
          "Changed from click-to-flip to hover-to-flip interaction",
          "Removed programmatic card back rendering in favor of SVG assets",
          "Maintained support for sm, md, lg size variants",
          "Preserved frozen card visual state handling",
        ],
      },
      {
        type: "design",
        category: "Visual Polish",
        items: [
          "Subtle branding text with reduced opacity for non-intrusive appearance",
          "Card shadow enhancement on hover for depth effect",
          "Consistent text sizing (10px) for 'Powered by' branding",
          "Proper spacing and alignment in footer sections",
        ],
      },
    ],
  },
  {
    version: "v0.5.0",
    date: "January 30, 2026",
    title: "Customer Portal & Mobile-First Fintech Redesign",
    description: "Complete customer-facing portal with OTP authentication, PayHub QR payments, 3D card visuals, and modern fintech app design (Cash App/Revolut style).",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Customer Portal Authentication",
        items: [
          "Phone-based OTP authentication with Twilio SMS integration",
          "Customer sessions management with secure session tokens",
          "Auto-create wallet on first signup for seamless onboarding",
          "CustomerProvider context for managing authentication state across portal",
          "Graceful fallback to console logging when Twilio unavailable",
        ],
      },
      {
        type: "added",
        category: "Customer Portal Pages",
        items: [
          "/portal/home - Feed-style homepage with balance, quick actions, and transaction feed",
          "/portal/wallet - Wallet management with balance display and transaction history",
          "/portal/cards - Card management with freeze/unfreeze and spending limits",
          "/portal/transactions - Unified transaction view with filtering",
          "/portal/account - Profile, security, and notification settings",
          "/portal/signin - OTP-based sign-in flow",
        ],
      },
      {
        type: "added",
        category: "PayHub QR Payments",
        items: [
          "Wallet top-up dialog with PayHub QR code generation",
          "Real-time payment status polling (3-second intervals)",
          "QR code download, copy, and share functionality",
          "UPI deep link support for Google Pay, PhonePe, Paytm",
          "Customer-facing payment status display with animations",
        ],
      },
      {
        type: "added",
        category: "3D Card Visuals",
        items: [
          "CardVisual component with interactive 3D hover effects",
          "VedaLogic branded card designs (SVG backgrounds)",
          "Mouse-tracking parallax rotation on hover",
          "Holographic shine effect with gradient overlays",
          "Premium EMV chip and contactless indicators",
          "Status badge display for frozen/cancelled/expired cards",
        ],
      },
      {
        type: "added",
        category: "Mobile-First Navigation",
        items: [
          "CustomerHeader - Top bar with hamburger menu, notifications, profile",
          "CustomerMenuDrawer - Slide-in navigation with profile summary",
          "CustomerBottomNav - Fixed bottom navigation bar (mobile only)",
          "useNavigation hook for scroll detection and active state management",
          "AnimatePresence page transitions for smooth navigation",
        ],
      },
      {
        type: "added",
        category: "Integrations Page",
        items: [
          "Standalone /integrations page (moved from Settings tab)",
          "India Market Partners card with 4 integrations (Host, PayHub, Signzy, Aadhaar)",
          "Expandable testing panels with click-to-expand rows",
          "PayHub testing panel with 5 tests (Connection, Payin, Payout, Balances)",
          "Signzy testing panel with 3 tests (Connection, Create Merchant, Status)",
          "Real-time test execution with loading states and result display",
        ],
      },
      {
        type: "added",
        category: "Database Schema Updates",
        items: [
          "customer_sessions table for customer authentication tokens",
          "otp_requests table for OTP tracking and verification",
          "avatar_url column added to customers table",
          "SIGNZY added to integration_provider enum",
          "Signzy integration records for all program managers",
        ],
      },
      {
        type: "added",
        category: "API Endpoints",
        items: [
          "POST /api/customer/auth/request-otp - Request OTP via SMS",
          "POST /api/customer/auth/verify-otp - Verify OTP and create session",
          "GET /api/customer/auth/me - Get authenticated customer info",
          "POST /api/customer/auth/logout - End customer session",
          "GET /api/customer/wallet - Get customer wallet details",
          "GET /api/customer/cards - Get customer cards",
          "POST /api/customer/cards/:id/freeze - Freeze customer card",
          "POST /api/customer/payhub/generate-qr - Generate payment QR code",
          "GET /api/topups - Get PayHub top-up transactions",
        ],
      },
      {
        type: "improved",
        category: "Modern Fintech Design",
        items: [
          "Complete portal redesign following Cash App/Revolut patterns",
          "Removed sidebar layout in favor of mobile-first navigation",
          "Hero sections with mesh gradient backgrounds",
          "AnimatedBalance component with number counting animation",
          "StatCard component with animated gradient displays",
          "TransactionTimeline with interactive grouping",
          "SpendingChart wrapper for Recharts visualizations",
        ],
      },
      {
        type: "improved",
        category: "Cards Page UX",
        items: [
          "3D card visuals with realistic hover effects",
          "Progress bars showing spending limit usage",
          "Animated freeze/unfreeze functionality",
          "Spending distribution chart visualization",
          "Empty state with large call-to-action button",
          "Responsive grid layout for all screen sizes",
        ],
      },
      {
        type: "improved",
        category: "Sidebar Navigation",
        items: [
          "Moved Platform Analytics to Operations section",
          "Added Integrations as top-level Operations link",
          "Cleaned up Dashboard section structure",
          "Updated role-based visibility for all user types",
        ],
      },
      {
        type: "fixed",
        category: "Bug Fixes",
        items: [
          "Fixed Transactions page ImpersonateButton causing router error",
          "Fixed dashboard undefined modal and cn import issues",
          "Fixed null/undefined handling in PayHub transaction data",
          "Fixed import path issues in integrations page",
          "Fixed customer portal logout to redirect to signin page",
        ],
      },
      {
        type: "design",
        category: "Animation System",
        items: [
          "Framer Motion animation utilities library",
          "Page transition variants (fade, slide, scale)",
          "Card hover effects with 3D transforms",
          "Balance counting animation with easing",
          "Smooth drawer and modal transitions",
        ],
      },
      {
        type: "security",
        category: "Customer Authentication",
        items: [
          "Secure OTP generation with 6-digit codes",
          "5-minute OTP expiration window",
          "Session token-based authentication",
          "Customer-specific API access control",
          "E.164 phone number formatting validation",
        ],
      },
    ],
  },
  {
    version: "v0.4.0",
    date: "January 28, 2026",
    title: "PayHub Payment Gateway Integration",
    description: "Complete integration with PayHub for India payments, including payin (customer payments), payout (disbursements), and balance management.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Payment Gateway Integration",
        items: [
          "PayHub service layer with token-based authentication and auto-refresh",
          "Payin Intent API for generating UPI deep links (Google Pay, PhonePe, Paytm)",
          "Payin Status endpoint for checking payment transaction status",
          "Payin Balance endpoint for checking available payment collection balance",
          "Payout Status endpoint for checking disbursement transaction status",
          "Payout Balance endpoint for checking available payout balance",
          "Minimum amount validation (Rs 100 minimum for PayHub transactions)",
        ],
      },
      {
        type: "added",
        category: "API Endpoints",
        items: [
          "POST /api/india/payments/payin-intent - Generate UPI payment links",
          "POST /api/payhub/payin-status - Check payment transaction status",
          "GET /api/payhub/payin-balance - Get available payin balance",
          "POST /api/payhub/payout-status - Check payout transaction status",
          "GET /api/payhub/payout-balance - Get available payout balance",
          "POST /api/payhub/test - Test PayHub connection and authentication",
          "POST /api/psps/gsx/webhook - Handle PayHub callback notifications",
        ],
      },
      {
        type: "added",
        category: "Admin Testing Panel",
        items: [
          "PayHub testing panel in Settings → Integrations for Super Admins",
          "Connection test to verify API authentication",
          "Payin Intent test to generate test UPI payment links",
          "Balance check tests for both payin and payout wallets",
          "Status check tests for transaction verification",
          "Real-time test results display with formatted responses",
          "Visual indicators for test success/failure states",
        ],
      },
      {
        type: "added",
        category: "Documentation",
        items: [
          "PayHub Admin Testing Guide for Project Managers",
          "PayHub Quick Reference card with API endpoints",
          "Vercel environment setup guide with step-by-step instructions",
          "PayHub URL structure documentation (sandbox vs production)",
          "Deployment guides with automated setup scripts",
        ],
      },
      {
        type: "improved",
        category: "Error Handling",
        items: [
          "Enhanced error logging for PayHub API requests",
          "Detailed error responses with specific failure reasons",
          "Configuration status checks before API calls",
          "Request/response logging for debugging on Vercel",
          "Graceful handling of authentication failures with auto-retry",
        ],
      },
      {
        type: "improved",
        category: "Settings Page",
        items: [
          "Collapsible integration testing panels for cleaner UI",
          "Real-time status indicators for payment gateway connections",
          "Improved test result display with formatted JSON responses",
          "Better visual hierarchy for testing actions",
          "Support for multiple payment gateway integrations",
        ],
      },
      {
        type: "fixed",
        category: "PayHub Integration",
        items: [
          "Corrected PayHub API endpoint paths (/user/sendpayinrequest)",
          "Fixed base URL usage (server.payhub.link for API, sandbox for callbacks)",
          "Resolved 404 errors by using correct endpoint structure",
          "Fixed amount format (string instead of number for PayHub API)",
          "Removed orphaned mock code causing build failures",
          "Fixed JavaScript reference error in testing panel UI",
        ],
      },
      {
        type: "security",
        category: "Payment Gateway Security",
        items: [
          "Secure token-based authentication with encrypted API keys",
          "Automatic token refresh before expiration",
          "Environment variable separation (local vs production credentials)",
          "Webhook signature validation for callback security",
          "Protected admin-only testing endpoints with requireAuth middleware",
        ],
      },
    ],
  },
  {
    version: "v0.3.0",
    date: "January 28, 2026",
    title: "Platform Analytics & Admin Features",
    description: "Added platform-wide analytics, user impersonation, and dark mode support.",
    isLatest: false,
    changes: [
      {
        type: "added",
        category: "Platform Analytics",
        items: [
          "Platform Analytics page with platform-wide KPIs (visible to Super Admins only)",
          "Organization and Organization Admin overview tables with entity counts",
          "Real-time statistics for customers, cards, transactions, and total volume",
        ],
      },
      {
        type: "added",
        category: "User Impersonation",
        items: [
          "Ability for Super Admins to impersonate Organizations, Organization Admins, or Customers",
          "Impersonation banner showing current impersonation status",
          "Session context switching to view platform from different user perspectives",
          "One-click exit from impersonation mode",
        ],
      },
      {
        type: "added",
        category: "Dark Mode",
        items: [
          "Platform-wide dark mode with complete color scheme",
          "Theme toggle in sidebar footer for quick access",
          "Appearance tab in Settings with visual theme preview cards",
          "System preference detection for automatic theme selection",
          "Theme persistence across sessions via localStorage",
        ],
      },
      {
        type: "added",
        category: "API Endpoints",
        items: [
          "Platform statistics endpoint with aggregated metrics",
          "All program managers endpoint with entity counts",
          "All corporate admins endpoint with customer/card counts",
          "Impersonation start/stop endpoints with session management",
        ],
      },
      {
        type: "improved",
        category: "Navigation",
        items: [
          "Platform Analytics link in sidebar for Super Admins",
          "Better visual hierarchy for admin-only features",
        ],
      },
    ],
  },
  {
    version: "v0.2.0",
    date: "January 27, 2026",
    title: "Documentation & UX Improvements",
    description: "Added platform documentation and refined user experience.",
    changes: [
      {
        type: "added",
        category: "Documentation",
        items: [
          "Platform Hierarchy documentation page with visual entity diagram",
          "Entity relationship explanations and ownership chain visualization",
          "Changelog page documenting all platform updates",
        ],
      },
      {
        type: "improved",
        category: "Navigation",
        items: [
          "Combined profile and sign out into single dropdown menu",
          "Added Docs link to sidebar navigation",
          "Removed sidebar collapse hover behavior for cleaner UX",
        ],
      },
    ],
  },
  {
    version: "v0.1.0",
    date: "January 27, 2026",
    title: "Major Feature Release",
    description: "Comprehensive platform enhancement with new pages, components, and backend improvements.",
    changes: [
      {
        type: "added",
        category: "Entity Detail Pages",
        items: [
          "Organization detail page with full entity information and related resources",
          "Organization Admin detail page with product management and customer overview",
          "Product detail page with configuration display and customer enrollment stats",
          "Customer detail page with KYC status, cards, and transaction history",
          "Card detail page with virtual card visual, wallet balance, and transaction log",
        ],
      },
      {
        type: "added",
        category: "New Pages",
        items: [
          "Consolidated Settings page with Profile, Team, and Integrations tabs",
          "Inventory management page with stock levels and order tracking",
          "Enhanced Report Center with downloadable reports and date filtering",
          "Customer Support dashboard with ticket management",
        ],
      },
      {
        type: "added",
        category: "Dialog Components",
        items: [
          "Create Organization dialog with form validation",
          "Create Organization Admin dialog with program manager selection",
          "Create Product dialog with card configuration options",
          "Create Customer dialog with address and identity fields",
          "Issue Card dialog with virtual card options",
          "Load Wallet dialog for adding funds to cards",
        ],
      },
      {
        type: "added",
        category: "Reusable Components",
        items: [
          "Virtual Card Visual component with realistic card display",
          "Activity Tab component for transaction and event history",
          "Related Resources component for entity relationships",
          "Status Filter component with multi-select filtering",
          "Tabbed Page component for consistent page layouts",
          "Detail Page component for entity detail views",
          "Inline Entity List component for embedded data tables",
        ],
      },
      {
        type: "added",
        category: "Backend Services",
        items: [
          "Email OTP authentication service for passwordless login",
          "TOTP (Time-based One-Time Password) service for 2FA",
          "Database seed script for demo data generation",
          "Enhanced PayHub integration with additional endpoints",
        ],
      },
      {
        type: "added",
        category: "API Endpoints",
        items: [
          "Dashboard summary KPIs endpoint",
          "Entity relationship endpoints (customers by product, cards by customer)",
          "Inventory management endpoints",
          "Report generation endpoints with CSV export",
          "Support ticket management endpoints",
        ],
      },
      {
        type: "improved",
        category: "Entity List Views",
        items: [
          "Organizations view with status badges and quick actions",
          "Organization Admins view with filtering and bulk operations",
          "Products view with card type indicators and enrollment counts",
          "Customers view with KYC status and card count display",
          "Cards view with status filtering and balance display",
        ],
      },
      {
        type: "improved",
        category: "Dashboard",
        items: [
          "Redesigned KPI cards with trend indicators",
          "Added transaction volume visualization",
          "Role-based metric visibility",
          "Quick action buttons for common tasks",
        ],
      },
      {
        type: "improved",
        category: "Login Experience",
        items: [
          "Modern login page design with gradient background",
          "Demo credentials section for easy testing",
          "Improved form validation and error messaging",
          "Loading states and smooth transitions",
        ],
      },
      {
        type: "design",
        category: "UI Components",
        items: [
          "Enhanced Button component with new variants and sizes",
          "Improved Badge component with semantic color variants",
          "Updated Card component with hover states and shadows",
          "Refined Input component with focus states",
          "Enhanced Table component with striped rows option",
        ],
      },
      {
        type: "design",
        category: "Design System",
        items: [
          "Extended color palette with semantic colors",
          "Custom shadow utilities including glow effects",
          "Smooth animation presets (fade, slide, scale)",
          "Gradient utilities for backgrounds and text",
          "Custom ring and focus state utilities",
        ],
      },
      {
        type: "design",
        category: "Sidebar Navigation",
        items: [
          "Two-level navigation structure for better organization",
          "Role-based menu visibility",
          "Active state indicators with animated borders",
          "Super Admin badge for elevated users",
          "Improved mobile responsiveness",
        ],
      },
      {
        type: "security",
        category: "API Security",
        items: [
          "Enhanced request validation middleware",
          "Improved error handling with sanitized responses",
          "Rate limiting on authentication endpoints",
          "Session validation improvements",
        ],
      },
      {
        type: "fixed",
        category: "Bug Fixes",
        items: [
          "Fixed sidebar scroll behavior on long navigation lists",
          "Corrected date formatting in transaction tables",
          "Fixed currency display for AUD amounts",
          "Resolved routing issues with nested entity pages",
        ],
      },
    ],
  },
  {
    version: "v0.0.1",
    date: "January 2026",
    title: "MVP Build - Initial Release",
    description: "Foundation of the VedaLogic Card Management Platform.",
    changes: [
      {
        type: "added",
        category: "Core Platform",
        items: [
          "User authentication with session management",
          "Role-based access control (Super Admin, Organization, Organization Admin, Finance)",
          "Basic dashboard with placeholder metrics",
          "Sidebar navigation structure",
        ],
      },
      {
        type: "added",
        category: "Entity Management",
        items: [
          "Organization CRUD operations",
          "Organization Admin CRUD operations",
          "Product CRUD operations",
          "Customer CRUD operations",
          "Card management basics",
        ],
      },
      {
        type: "added",
        category: "Technical Foundation",
        items: [
          "React + TypeScript frontend with Vite",
          "Express.js backend with TypeScript",
          "PostgreSQL database with Drizzle ORM",
          "TanStack Query for data fetching",
          "Tailwind CSS with shadcn/ui components",
          "Wouter for client-side routing",
        ],
      },
      {
        type: "added",
        category: "Infrastructure",
        items: [
          "Vercel deployment configuration",
          "Environment-based configuration",
          "Database migration system",
          "API proxy for development",
        ],
      },
    ],
  },
];

export default function Changelog() {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Changelog</h1>
        <p className="text-muted-foreground mt-2">
          All notable changes and updates to the VedaLogic Card Management Platform
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        {[
          { label: "New Features", value: "120+", icon: Sparkles, color: "text-emerald-500" },
          { label: "Components", value: "40+", icon: LayoutGrid, color: "text-blue-500" },
          { label: "API Endpoints", value: "90+", icon: Database, color: "text-violet-500" },
          { label: "Pages", value: "35+", icon: FileCode, color: "text-amber-500" },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <stat.icon className={cn("h-8 w-8", stat.color)} />
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Jump */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Jump to Version</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {changelog.map((entry) => {
              const versionId = entry.version.replace(/\./g, "-");
              return (
                <Badge
                  key={entry.version}
                  variant={entry.isLatest ? "default" : "outline"}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => {
                    const element = document.getElementById(versionId);
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                >
                  {entry.version}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-0">
            {changelog.map((entry) => (
              <ChangelogEntry key={entry.version} {...entry} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Change Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(typeConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <Badge key={key} variant="outline" className={cn("gap-1.5", config.color)}>
                  <Icon className="h-3 w-3" />
                  {config.label}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
